// Discado corto: emergencias, informativos y servicios (netvoice.service_numbers).
// Los cambios toman efecto en la PRÓXIMA llamada, sin recargar Asterisk: el dialplan
// resuelve el número con el AGI service_number.py, que lee esta misma tabla.
import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";

const TIPOS = [
  { v: "EMERGENCY", label: "Emergencia" },
  { v: "GENERAL",   label: "Informativo / servicio" },
  { v: "TELEPHONY", label: "Código de función" },
];

// El color es de ESTADO, no decorativo: rojo = emergencia (no se toca a la ligera),
// ámbar = configurado pero no cursa, gris = desactivado.
const chipTipo = (t) => ({
  EMERGENCY: { bg: "var(--danger-bg)",  bd: "var(--danger)",  fg: "var(--danger)",  txt: "Emergencia" },
  GENERAL:   { bg: "var(--info-bg)",    bd: "var(--info)",    fg: "var(--info)",    txt: "Servicio" },
  TELEPHONY: { bg: "var(--bg-raised)",  bd: "var(--border)",  fg: "var(--text-secondary)", txt: "Función" },
}[t] || { bg: "var(--bg-raised)", bd: "var(--border)", fg: "var(--text-secondary)", txt: t });

const Chip = ({ bg, bd, fg, children, title }) => (
  <span title={title} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10.5,
    fontWeight:600, padding:"2.5px 8px", borderRadius:999, background:bg,
    border:`1px solid ${bd}`, color:fg, whiteSpace:"nowrap" }}>{children}</span>
);

const VACIO = { short_number:"", forward_number:"", descripcion:"", service_type:"GENERAL",
                ani_pattern:"%", activo:"yes", observaciones:"" };

function Modal({ item, onClose, onSaved }) {
  const editando = !!item?.id;
  const [f, setF]     = useState(item ? { ...VACIO, ...item, forward_number: item.forward_number || "" } : VACIO);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const guardar = async () => {
    setBusy(true); setErr(null);
    try {
      // Desactivar o degradar una emergencia exige confirmación explícita en el backend.
      const necesitaConfirmar = editando && item.service_type === "EMERGENCY" &&
                                (f.activo === "no" || f.service_type !== "EMERGENCY");
      if (necesitaConfirmar && !(await confirmar(
            `${item.short_number} es un número de EMERGENCIA.\n\n` +
            `Si lo desactivás, los clientes dejan de poder marcarlo. ¿Confirmás?`))) {
        setBusy(false); return;
      }
      const q = necesitaConfirmar ? "?confirmar=true" : "";
      if (editando) await api.put(`/v1/service-numbers/${item.id}${q}`, f);
      else          await api.post("/v1/service-numbers", f);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const esEmerg = f.service_type === "EMERGENCY";
  const inp = { padding:"7px 9px", borderRadius:"var(--r-sm)", border:"1px solid var(--border)",
                background:"var(--bg-raised)", color:"var(--text-primary)", fontSize:12.5, width:"100%" };
  const lbl = { display:"flex", flexDirection:"column", gap:5, fontSize:11, color:"var(--text-secondary)" };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(4,8,16,.7)",
      display:"grid", placeItems:"center", zIndex:200, padding:16 }}>
      {dialogoConfirm}
      <div onClick={e => e.stopPropagation()} className="nv-card"
        style={{ width:"min(560px,100%)", padding:0, overflow:"hidden" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--border)" }}>
          <div style={{ fontSize:14, fontWeight:600 }}>
            {editando ? `Editar ${item.short_number}` : "Nuevo número corto"}
          </div>
        </div>
        <div style={{ padding:18, display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:14 }}>
            <label style={lbl}>Número corto
              <input style={{ ...inp, fontFamily:"var(--font-mono)" }} value={f.short_number}
                placeholder="911 · 100 · *26"
                onChange={e => set("short_number", e.target.value.replace(/[^0-9*#]/g, ""))}/>
            </label>
            <label style={lbl}>Tipo
              <select style={inp} value={f.service_type} onChange={e => set("service_type", e.target.value)}>
                {TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <label style={lbl}>Destino real
            <input style={{ ...inp, fontFamily:"var(--font-mono)" }} value={f.forward_number}
              placeholder={esEmerg ? "obligatorio (ej. 5934911)" : "vacío = no cursa llamada"}
              onChange={e => set("forward_number", e.target.value.replace(/[^0-9*#]/g, ""))}/>
            <span style={{ fontSize:10.5, color:"var(--text-muted)" }}>
              A qué número se traduce antes de rutear. Si lo dejás vacío, el número queda
              registrado pero la llamada se rechaza con causa (no cursa).
            </span>
          </label>
          <label style={lbl}>Descripción
            <input style={inp} value={f.descripcion} placeholder="Bomberos"
              onChange={e => set("descripcion", e.target.value)}/>
          </label>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:14 }}>
            <label style={lbl}>ANIs habilitados
              <input style={{ ...inp, fontFamily:"var(--font-mono)" }} value={f.ani_pattern}
                placeholder="%" onChange={e => set("ani_pattern", e.target.value)}/>
            </label>
            <label style={lbl}>Estado
              <select style={inp} value={f.activo} onChange={e => set("activo", e.target.value)}>
                <option value="yes">Activo</option><option value="no">Desactivado</option>
              </select>
            </label>
          </div>
          {esEmerg && (
            <div style={{ fontSize:11.5, color:"var(--text-secondary)", background:"var(--danger-bg)",
              border:"1px solid var(--danger)", borderRadius:"var(--r-sm)", padding:"10px 12px", lineHeight:1.55 }}>
              <strong style={{ color:"var(--danger)" }}>Es una emergencia.</strong> Se cursa
              siempre: no la detiene el corte por impago, ni los permisos del ANI, ni el
              antifraude. Y si la base se cae, el ruteo la resuelve con un respaldo en código.
            </div>
          )}
          {err && <div style={{ color:"var(--danger)", fontSize:12 }}>{err}</div>}
          <div style={{ display:"flex", gap:9, justifyContent:"flex-end" }}>
            <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="nv-btn nv-btn-primary" disabled={busy || !f.short_number || !f.descripcion}
              onClick={guardar}>{busy ? "Guardando…" : "Guardar"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DiscadoCorto() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [modal, setModal]     = useState(null);
  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();

  const cargar = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/v1/service-numbers");
      setRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch (e) {
      setErr(e.response?.data?.detail || "No se pudo cargar el discado corto");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const borrar = async (r) => {
    const emerg = r.service_type === "EMERGENCY";
    const msg = emerg
      ? `${r.short_number} es un número de EMERGENCIA (${r.descripcion}).\n\n` +
        `Al borrarlo, los clientes dejan de poder marcarlo. ¿Confirmás?`
      : `¿Borrar ${r.short_number} (${r.descripcion})?`;
    if (!(await confirmar(msg))) return;
    try {
      await api.delete(`/v1/service-numbers/${r.id}${emerg ? "?confirmar=true" : ""}`);
      cargar();
    } catch (e) { setErr(e.response?.data?.detail || "No se pudo borrar"); }
  };

  const grupos = TIPOS.map(t => ({ ...t, items: rows.filter(r => r.service_type === t.v) }))
                      .filter(g => g.items.length > 0);
  const emergencias = rows.filter(r => r.service_type === "EMERGENCY" && r.activo === "yes").length;

  const th = { textAlign:"left", fontSize:10, fontWeight:700, letterSpacing:".09em",
               textTransform:"uppercase", color:"var(--text-muted)", padding:"9px 14px",
               borderBottom:"1px solid var(--border)" };
  const td = { padding:"10px 14px", borderBottom:"1px solid var(--border)", fontSize:12.5 };

  return (
    <div style={{ padding:"24px 28px", maxWidth:1040 }}>
      {dialogoConfirm}

      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
                    gap:16, marginBottom:18 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:600 }}>Discado corto</h2>
          <p style={{ margin:"4px 0 0", color:"var(--text-secondary)", fontSize:13, maxWidth:"62ch" }}>
            Números que el cliente marca en forma abreviada: emergencias, informativos y
            códigos de función. Se resuelven antes del ruteo y los cambios aplican en la
            próxima llamada, sin reiniciar nada.
          </p>
        </div>
        <button className="nv-btn nv-btn-primary" onClick={() => setModal({})}>+ Nuevo número</button>
      </div>

      {emergencias === 0 && !loading && (
        <div style={{ marginBottom:14, padding:"12px 14px", background:"var(--danger-bg)",
          border:"1px solid var(--danger)", borderRadius:"var(--r-md)", fontSize:12.5 }}>
          <strong style={{ color:"var(--danger)" }}>No hay ninguna emergencia activa.</strong>{" "}
          <span style={{ color:"var(--text-secondary)" }}>
            Los clientes no pueden marcar 911 ni 100. Cargá al menos las emergencias antes de
            dar de alta clientes.
          </span>
        </div>
      )}

      {err && <div style={{ marginBottom:12, color:"var(--danger)", fontSize:12.5 }}>{err}</div>}

      {loading ? (
        <div className="nv-card" style={{ padding:40, textAlign:"center", color:"var(--text-secondary)" }}>
          Cargando…
        </div>
      ) : rows.length === 0 ? (
        <div className="nv-card" style={{ padding:40, textAlign:"center" }}>
          <div style={{ color:"var(--text-secondary)", fontSize:13 }}>
            Todavía no hay números cortos configurados.
          </div>
          <button className="nv-btn nv-btn-primary" style={{ marginTop:14 }}
            onClick={() => setModal({})}>Cargar el primero</button>
        </div>
      ) : grupos.map(g => (
        <div key={g.v} style={{ marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:8 }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:".13em",
              textTransform:"uppercase", color:"var(--text-muted)" }}>{g.label}</span>
            <span style={{ fontSize:11, color:"var(--text-muted)" }}>{g.items.length}</span>
          </div>
          <div className="nv-card" style={{ padding:0, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  <th style={th}>Marca</th><th style={th}>Va a</th><th style={th}>Descripción</th>
                  <th style={th}>ANIs</th><th style={th}>Estado</th><th style={{ ...th, textAlign:"right" }}></th>
                </tr></thead>
                <tbody>
                  {g.items.map(r => {
                    const c = chipTipo(r.service_type);
                    const cursa = !!r.forward_number;
                    const inactivo = r.activo !== "yes";
                    return (
                      <tr key={r.id} style={{ opacity: inactivo ? .5 : 1 }}>
                        <td style={{ ...td, fontFamily:"var(--font-mono)", fontWeight:600,
                                     fontVariantNumeric:"tabular-nums" }}>{r.short_number}</td>
                        <td style={{ ...td, fontFamily:"var(--font-mono)", fontVariantNumeric:"tabular-nums" }}>
                          {cursa ? r.forward_number : (
                            <Chip bg="var(--warning-bg)" bd="var(--warning)" fg="var(--warning)"
                              title="Sin destino: el número está registrado pero la llamada se rechaza con causa">
                              no cursa
                            </Chip>
                          )}
                        </td>
                        <td style={{ ...td, color:"var(--text-secondary)" }}>
                          {r.descripcion}
                          {r.observaciones && (
                            <div style={{ fontSize:10.5, color:"var(--text-muted)", marginTop:2 }}>
                              {r.observaciones}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, fontFamily:"var(--font-mono)", color:"var(--text-muted)" }}>
                          {r.ani_pattern === "%" ? "todos" : r.ani_pattern}
                        </td>
                        <td style={td}>
                          {inactivo
                            ? <Chip bg="var(--bg-raised)" bd="var(--border)" fg="var(--text-muted)">Desactivado</Chip>
                            : <Chip bg={c.bg} bd={c.bd} fg={c.fg}>{c.txt}</Chip>}
                        </td>
                        <td style={{ ...td, textAlign:"right", whiteSpace:"nowrap" }}>
                          <button className="nv-btn nv-btn-ghost" style={{ padding:"4px 10px", fontSize:11.5 }}
                            onClick={() => setModal(r)}>Editar</button>
                          <button className="nv-btn nv-btn-ghost" style={{ padding:"4px 10px", fontSize:11.5,
                            color:"var(--danger)" }} onClick={() => borrar(r)}>Borrar</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      <div style={{ marginTop:6, padding:"12px 14px", background:"var(--bg-surface)",
        borderRadius:"var(--r-md)", fontSize:11.5, color:"var(--text-secondary)", lineHeight:1.65 }}>
        <strong style={{ color:"var(--text-primary)" }}>Cómo funciona.</strong> El ruteo consulta
        esta tabla solo cuando el marcado puede ser corto (hasta 4 dígitos, o empieza con
        <code> * </code>o<code> # </code>), así una llamada normal no paga el costo.{" "}
        <strong style={{ color:"var(--danger)" }}>Las emergencias se cursan siempre:</strong> no
        las detiene el corte por impago, ni los permisos del ANI, ni el antifraude — y si la
        base se cae, el ruteo las resuelve con un respaldo en código.
      </div>

      {modal && <Modal item={modal.id ? modal : null} onClose={() => setModal(null)}
                       onSaved={() => { setModal(null); cargar(); }}/>}
    </div>
  );
}
